import React, { useState, useCallback, memo } from "react";
import { Row } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Login.css";
import instance from "../../Api";
import { useAuth } from "../../AuthContext";
import Loader from "../common/Loader";
import toast, { Toaster } from "react-hot-toast";
import AppToast from "../../AppToast";

// ─── Shared sub-components ────────────────────────────────────────────────────
const EyeToggle = memo(({ show, onToggle }) =>
  show
    ? <FaEyeSlash onClick={onToggle} style={{ color: "white", width: "20px" }} />
    : <FaEye     onClick={onToggle} style={{ color: "white", width: "20px" }} />
);

const PasswordField = memo(({ value, show, onToggle, onChange, placeholder, id }) => (
  <div style={{ marginTop: "15px", display: "flex", alignItems: "center", border: "2px solid white", borderRadius: "25px", padding: "5px" }}>
    <img src="/images/lock.svg" alt="" style={{ width: "15px", height: "15px", marginRight: "5px" }} />
    <input
      value={value ?? ""}
      type={show ? "text" : "password"}
      id={id}
      className="form-control white-placeholder"
      name={id}
      placeholder={placeholder}
      style={{ color: "white", border: "none", outline: "none", background: "transparent", flex: "1", padding: "5px" }}
      onChange={(e) => onChange(e.target.value)}
    />
    <EyeToggle show={show} onToggle={onToggle} />
  </div>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const ChangePassword = ({ isMobile }) => {
  const { smuser } = useAuth();

  const [showCurrPass, setShowCurrPass] = useState(false);
  const [showNewPass,  setShowNewPass]  = useState(false);
  const [showConfPass, setShowConfPass] = useState(false);
  const [loading,      setLoading]      = useState(false);

  // Fix: initial state was null, not "". Original validation used `!== ""`
  // which passes for null (`null !== ""` is true), so the API was called
  // with all-null fields. Changed initial state to "" and validation to !value.
  const [currPass,    setCurrPass]    = useState("");
  const [newPass,     setNewPass]     = useState("");
  const [newConfPass, setNewConfpass] = useState("");

  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />),
    [isMobile]);

  const ChangePassCall = useCallback(async () => {
    // Fix: original checked `currPass !== ""` but initial state was null.
    // `null !== ""` is true — validation always passed with empty fields.
    if (!currPass || !newPass || !newConfPass) {
      showToast("All fields are required!");
      return;
    }
    if (newPass !== newConfPass) {
      showToast("Password and Confirm Password are not matched!");
      return;
    }
    setLoading(true);
    try {
      const res = await instance.post("/changepassword", {
        user_id: smuser.id,
        current_password: currPass,
        new_password: newPass,
        new_confirm_password: newConfPass,
      });
      showToast(res.data.message);
    } catch {
      showToast("Oops! Something went wrong while fetching the data. Please try again later or contact support if the issue persists.");
    }
    setLoading(false);
  }, [currPass, newPass, newConfPass, smuser?.id, showToast]);

  // ── Shared form — defined once, rendered in both mobile and web ───────────
  // Original duplicated all three password fields + button identically for mobile/web.
  const form = (
    <>
      <PasswordField value={currPass}    show={showCurrPass} onToggle={() => setShowCurrPass((p) => !p)} onChange={setCurrPass}    placeholder="Current Password"  id="current-password" />
      <PasswordField value={newPass}     show={showNewPass}  onToggle={() => setShowNewPass((p) => !p)}  onChange={setNewPass}     placeholder="New Password"      id="new-password" />
      <PasswordField value={newConfPass} show={showConfPass} onToggle={() => setShowConfPass((p) => !p)} onChange={setNewConfpass} placeholder="Confirm Password"   id="confirm-password" />

      <div className="log-in" style={{ marginTop: "15px", marginLeft: isMobile ? "5%" : "-8rem", justifyContent: "center" }} onClick={ChangePassCall}>
        <a href="#" className="media-link">
          <div className="media-banner" style={{ width: "auto", height: "50px", marginTop: "10px", marginLeft: isMobile ? "5rem" : "10rem" }}>
            <img className="normal-banner"    src="/images/signup-btn-bg.png"     alt="" style={{ marginTop: "-6px", width: isMobile ? "11rem" : "12rem", height: "4rem" }} />
            <img className="hover-img-banner" src="/images/search-btn-hover.png" alt="" style={{ marginTop: "-6px", width: isMobile ? "11rem" : "12rem", height: "4rem" }} />
            <span className="login-text" style={{ fontSize: "20px", color: "#07528B", marginTop: "0px" }}>Update Password</span>
          </div>
        </a>
      </div>
    </>
  );

  const cardStyle = (top) => ({
    backgroundImage: `url("/images/blue-box-bg.svg")`,
    backgroundSize: "cover",
    top,
    width: "90%",
    marginLeft: "25px",
    padding: "30px 25px",
    position: "relative",
    backgroundColor: "#07528b",
    borderRadius: "12px",
    flexWrap: "wrap",
  });

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", minHeight: "55rem", position: "relative", backgroundImage: "url(./main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>
      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────── */
        <>
          <div className="heading-dotted-changepass">Change Password <span /></div>
          <div style={cardStyle("6rem")}>{form}</div>
        </>
      ) : (
        /* ── WEB ─────────────────────────────────────────────────────────── */
        <>
          <div className="heading-dotted-changepass" style={{ marginLeft: "16rem" }}>Change Password <span /></div>
          <Row>
            <div className="col-md-4" />
            <div className="col-md-4">
              <div style={cardStyle("10rem")}>
                <div style={{ display: "grid", justifyItems: "stretch" }}>
                  {form}
                </div>
              </div>
            </div>
            <div className="col-md-4" />
          </Row>
        </>
      )}

      {loading && (
        <div className="loading-overlay" style={{ zIndex: "9999999999999999" }}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  );
};

export default ChangePassword;